// ==============================================
// Admin · Scout Onboarding Funnel
// ==============================================
//
// Reads GET /api/admin/onboarding/devices — one row per Scout, per-step
// latest event of the device's current run (status + timestamp + GPS),
// segment pills (server-side), search, per-row out-of-box reset (typed
// confirmation; two-phase — executes when the device acks) and today's
// offline unlock code. Pattern: AdminHardwareOrdersPage pill/table
// skeleton + AdminUsagePage stat tiles.

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RocketLaunchIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  ClockIcon,
  KeyIcon,
  ArrowUturnLeftIcon,
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  TrashIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type {
  OnboardingFunnelRow,
  OnboardingRun,
  OnboardingFunnelSegment,
  OnboardingStepCell,
  OnboardingStepKey,
} from '../../services/api';
import { ModalShell } from '../../components/ui/ModalShell';
import { TypedConfirmModal } from '../../components/ui/TypedConfirmModal';
import { useToastStore } from '../../store/toastStore';

const SEGMENTS: { key: OnboardingFunnelSegment; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'no_account', label: 'No account yet' },
  { key: 'no_plan', label: 'No plan yet' },
  { key: 'stalled', label: 'Stalled > 24 h' },
  { key: 'complete', label: 'Complete' },
  { key: 'in_transit', label: 'In transit' },
  { key: 'not_booted', label: 'Not yet booted' },
  { key: 'anomaly', label: '⚠ Needs attention' },
];

// Supply-chain anomalies noticed when a unit reaches the internet. None of
// these block a customer; they exist so a unit that turns up somewhere
// unexpected is visible rather than silent.
const ANOMALY_COPY: Record<string, { label: string; detail: string }> = {
  live_before_shipping: {
    label: 'Live but never shipped',
    detail: 'This unit came online while inventory still had it on the bench. Either the paperwork is behind, or it left the building without being shipped.',
  },
  no_allocation: {
    label: 'Shipped with no order',
    detail: 'Marked shipped and now live, but no allocation says who it was going to.',
  },
  recipient_mismatch: {
    label: 'Signed in by someone else',
    detail: 'The account that signed this Scout in is not the one it shipped to. Often legitimate (a gift, a fleet manager, a resale) — worth a look.',
  },
};

const STEPS: { key: OnboardingStepKey; label: string }[] = [
  { key: 'antenna', label: '1 · Antenna' },
  { key: 'wifi', label: '2 · Wi-Fi' },
  { key: 'update', label: '3 · Update' },
  { key: 'signin', label: '4 · Sign-in' },
  { key: 'plan', label: '5 · Plan' },
  // The completing step: its ✓ is the customer's recorded acceptance of the
  // Terms of Use and the care-of-Scout guidance.
  { key: 'care', label: '6 · Care & Terms' },
];

const PAGE_SIZE = 50;

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-NZ', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function fmtPos(pos: { lat: number; lon: number } | null | undefined): string | null {
  if (!pos || typeof pos.lat !== 'number' || typeof pos.lon !== 'number') return null;
  const lat = `${Math.abs(pos.lat).toFixed(2)}°${pos.lat < 0 ? 'S' : 'N'}`;
  const lon = `${Math.abs(pos.lon).toFixed(2)}°${pos.lon < 0 ? 'W' : 'E'}`;
  return `${lat} ${lon}`;
}

/** The network a Wi-Fi event was stamped against, if the device reported one. */
function cellSsid(cell: OnboardingStepCell | undefined): string | null {
  const ssid = cell?.details?.ssid;
  return typeof ssid === 'string' && ssid.trim() ? ssid : null;
}

/**
 * Step 6 recorded by an admin rather than by the Scout. Launchers up to
 * 2.49.13 could lose the customer's care/verified tap on the way out, so the
 * acceptance the customer reported is entered by hand — and must always read
 * differently from the device's own proof.
 */
function recordedByAdmin(details: Record<string, unknown> | null | undefined): boolean {
  return details?.recorded_by_admin === true;
}

/** A plan event this late can only have been carried out by the care-tap flush (mirrors the API's rule). */
const ESTIMATE_MIN_LAG_MS = 3_000;

/**
 * The customer's estimated tap time behind an admin-recorded step 6, or null
 * when only the admin's clock is known. Mirrors the API's derivation so the
 * run history (which sees raw events) agrees with the funnel row.
 */
function estimatedAcceptanceAt(
  care: { occurred_at: string; details: Record<string, unknown> | null } | undefined,
  plan: { occurred_at: string; received_at: string } | undefined,
): string | null {
  if (!care || !recordedByAdmin(care.details)) return null;
  const basis = care.details?.acceptance_time_basis;
  if (basis === 'estimated_from_plan_delivery') return care.occurred_at;
  if (basis === 'admin_recorded_time') return null;
  if (!plan) return null;
  const lagMs = new Date(plan.received_at).getTime() - new Date(plan.occurred_at).getTime();
  return Number.isFinite(lagMs) && lagMs >= ESTIMATE_MIN_LAG_MS ? plan.received_at : null;
}

/**
 * Only a signed-in Scout that verified its plan can have shown the care card.
 * Decided server-side across ALL runs: the row's visible steps are the latest
 * run only, and on launchers up to 2.49.12 that is often a boot-time phantom
 * (wifi + update) sitting on top of the run the customer actually completed.
 */
function canRecordAcceptance(row: OnboardingFunnelRow): boolean {
  return row.admin_acceptance_eligible === true;
}

// Step cell: verified=green, bypassed=amber (self-report without proof),
// started=cyan pulse-ish, stalled=red, absent=dash.
//
// Every status shows when it happened, not just verified — a bypassed antenna
// or a step that started and never finished is exactly when you most want to
// know the time it did so.
function StepCell({
  row,
  step,
  onShowMap,
}: {
  row: OnboardingFunnelRow;
  step: OnboardingStepKey;
  onShowMap?: (row: OnboardingFunnelRow) => void;
}) {
  const cell = row.steps[step];
  if (!cell) return <span className="text-slate-600">—</span>;
  const pos = fmtPos(cell.position);
  const ssid = step === 'wifi' ? cellSsid(cell) : null;
  // Where it came online. The Wi-Fi event's own stamp when there was a fix;
  // otherwise the device's last known position, which is only ever approximate.
  const mapPos = step === 'wifi' ? (cell.position ?? row.last_position) : null;
  const styles: Record<string, string> = {
    verified: 'bg-green-500/10 text-green-400',
    bypassed: 'bg-amber-500/10 text-amber-400',
    started: 'bg-cyan-500/10 text-cyan-300',
    stalled: 'bg-red-500/10 text-red-400',
  };
  const labels: Record<string, string> = {
    verified: '✓',
    bypassed: '⚠ bypassed',
    started: '⋯ started',
    stalled: '✗ stalled',
  };
  const adminCare = step === 'care' && recordedByAdmin(cell.details);
  const adminEmail = adminCare && typeof cell.details?.admin_email === 'string' ? cell.details.admin_email : null;
  // The server derives the estimate (stored on new events, computed from the
  // plan event for the ones recorded before the estimate existed).
  const estimatedAt = adminCare ? row.admin_acceptance_estimated_at : null;
  // When the admin recorded it: new events say so in details; older ones have
  // the admin's clock as the event time itself.
  const recordedAt = adminCare ? (typeof cell.details?.recorded_at === 'string' ? cell.details.recorded_at : cell.occurred_at) : null;
  // "≈" when the shown time is the customer's tap recovered from the Scout's
  // own traffic; "✓" when it is simply the moment the admin recorded it.
  const label = adminCare ? (estimatedAt ? '≈ admin-recorded' : '✓ admin-recorded') : (labels[cell.status] ?? cell.status);
  const shownTime = estimatedAt ?? cell.occurred_at;
  const adminTitle = adminCare
    ? ` · recorded by ${adminEmail ?? 'an admin'}${recordedAt ? ` on ${fmtTime(recordedAt)}` : ''} — ${estimatedAt ? 'time is the customer\'s tap, estimated from when the Scout delivered its plan event' : 'time is when the admin recorded it'} — not the device's own proof`
    : '';
  return (
    <div title={`${cell.status} · device time ${fmtTime(cell.occurred_at)} · received ${fmtTime(cell.received_at)}${pos ? ` · ${pos}` : ''}${adminTitle}`}>
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${styles[cell.status] ?? 'bg-slate-700 text-slate-300'}`}>
        {label} {fmtTime(shownTime)}
      </span>
      {ssid && <div className="text-[11px] text-slate-300 mt-0.5 truncate max-w-[11rem]" title={ssid}>{ssid}</div>}
      {pos && <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{pos}</div>}
      {mapPos && onShowMap && (
        <button
          type="button"
          onClick={() => onShowMap(row)}
          className="mt-1 inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300 hover:underline"
        >
          <MapPinIcon className="w-3 h-3" />
          Map
        </button>
      )}
    </div>
  );
}

function overallChip(row: OnboardingFunnelRow): { label: string; cls: string } {
  if (row.reset_pending) return { label: 'RESET PENDING', cls: 'bg-purple-500/10 text-purple-300' };
  if (row.completed_at) {
    return (row.care_recorded_by_admin || recordedByAdmin(row.steps.care?.details))
      ? { label: 'COMPLETE · ADMIN-RECORDED', cls: 'bg-green-500/10 text-green-400' }
      : { label: 'COMPLETE', cls: 'bg-green-500/10 text-green-400' };
  }
  if (!row.first_contact_at) {
    return row.stock_state === 'shipped'
      ? { label: 'IN TRANSIT', cls: 'bg-blue-500/10 text-blue-300' }
      : { label: 'NOT YET BOOTED', cls: 'bg-slate-700/60 text-slate-400' };
  }
  if (!row.last_event_at) return { label: 'NOT YET BOOTED', cls: 'bg-slate-700/60 text-slate-400' };
  const ageMs = Date.now() - new Date(row.last_event_at).getTime();
  if (ageMs > 24 * 3600 * 1000) {
    const days = Math.floor(ageMs / (24 * 3600 * 1000));
    return { label: `STALLED ${days}d`, cls: 'bg-red-500/10 text-red-400' };
  }
  return { label: `IN PROGRESS${row.current_step ? ` · ${row.current_step}` : ''}`, cls: 'bg-amber-500/10 text-amber-300' };
}

export function AdminOnboardingPage() {
  const queryClient = useQueryClient();
  // Handed to the device detail page so its back button returns here rather
  // than to the generic device list. Segment/search live in local state, so
  // this restores the page, not the exact filter.
  const location = useLocation();
  const backTo = location.pathname + location.search;
  const [mapRow, setMapRow] = useState<OnboardingFunnelRow | null>(null);
  const [historyRow, setHistoryRow] = useState<OnboardingFunnelRow | null>(null);
  const [acceptTarget, setAcceptTarget] = useState<OnboardingFunnelRow | null>(null);
  const [acceptNote, setAcceptNote] = useState('');
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [segment, setSegment] = useState<OnboardingFunnelSegment>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [resetTarget, setResetTarget] = useState<OnboardingFunnelRow | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [cancelBusyId, setCancelBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OnboardingFunnelRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setPage(1), [segment, debouncedSearch]);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'onboarding', segment, debouncedSearch, page],
    queryFn: () => api.adminGetOnboardingDevices({
      segment,
      search: debouncedSearch || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    refetchInterval: 30000,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleReset = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      const result = await api.adminResetOnboardingDevice(resetTarget.device_id);
      useToastStore.getState().addToast({
        type: 'success',
        title: result.already_pending ? 'Reset already queued' : 'Reset queued',
        message: `${resetTarget.serial_number} returns to out-of-box at its next check-in. Setup history stays in the audit trail.`,
      });
      setResetTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
    } catch {
      // Global axios interceptor already raised an error toast.
    } finally {
      setResetBusy(false);
    }
  };

  /**
   * Withdraw a queued reset. Needed because a reset only lands when the Scout
   * is online AND it forgets the device's saved networks — so a reset queued
   * against a just-reset unit lies in wait and fires mid-walk, wiping the
   * customer's progress and forgetting the networks again.
   */
  const handleCancelReset = async (row: OnboardingFunnelRow) => {
    setCancelBusyId(row.device_id);
    try {
      const result = await api.adminCancelOnboardingReset(row.device_id);
      useToastStore.getState().addToast({
        type: 'success',
        title: result.cancelled > 0 ? 'Reset cancelled' : 'Nothing was queued',
        message: `${row.serial_number} will keep its current setup progress.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
    } catch {
      // Global axios interceptor already raised an error toast.
    } finally {
      setCancelBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await api.adminDeleteOnboardingDevice(deleteTarget.device_id);
      useToastStore.getState().addToast({
        type: 'success',
        title: 'Device deleted',
        message: `${deleteTarget.serial_number} is gone from the fleet. Its build record stays in inventory.`,
      });
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
    } catch {
      // Global axios interceptor already raised an error toast.
    } finally {
      setDeleteBusy(false);
    }
  };

  /**
   * Step 6 on the admin's say-so. The customer accepted on the Scout but the
   * event never reached us (launcher event-queue bug, vortexnav-client #636);
   * the server stores it marked recorded_by_admin so it never passes as the
   * device's own proof.
   */
  const handleRecordAcceptance = async () => {
    if (!acceptTarget) return;
    setAcceptBusy(true);
    try {
      await api.adminRecordOnboardingAcceptance(acceptTarget.device_id, acceptNote.trim() || undefined);
      useToastStore.getState().addToast({
        type: 'success',
        title: 'Acceptance recorded',
        message: `${acceptTarget.serial_number} is now complete. Step 6 shows as admin-recorded, not as the Scout's own event.`,
      });
      setAcceptTarget(null);
      setAcceptNote('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
    } catch {
      // Global axios interceptor already raised an error toast (409 carries the reason).
    } finally {
      setAcceptBusy(false);
    }
  };

  const handleUnlockCode = async (row: OnboardingFunnelRow) => {
    try {
      const result = await api.adminGetOnboardingUnlockCode(row.device_id);
      try {
        await navigator.clipboard.writeText(result.code);
      } catch {
        // Clipboard unavailable (permissions) — the toast still shows the code.
      }
      useToastStore.getState().addToast({
        type: 'success',
        title: `Unlock code for ${result.serial_number}: ${result.code}`,
        message: `Copied to clipboard. Valid for UTC day ${result.valid_for_utc_day}; read it to the customer, they enter it on the Scout's hidden support screen (7 taps on the serial).`,
      });
    } catch {
      // Interceptor toast covers failure (e.g. secret not configured → 503).
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <RocketLaunchIcon className="w-6 h-6 text-cyan-400" />
            Scout Onboarding
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            First-boot setup funnel, keyed by the unit&rsquo;s factory Wi-Fi MAC — the same identifier the build station stamps.
            A Scout appears here the moment it reaches the internet, resolved to its inventory record, before any account exists.
            Step cells show the current run with the GPS position each step was stamped at.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:border-slate-600"
        >
          <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Segment pills + search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {SEGMENTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSegment(s.key)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                segment === s.key
                  ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300'
                  : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <MagnifyingGlassIcon className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Serial, name, or email…"
            className="pl-9 pr-3 py-1.5 w-64 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400">Loading…</div>
        ) : isError ? (
          <div className="p-8 text-center text-red-400">Failed to load the onboarding funnel.</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No Scouts in this segment.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-700">
                  <th className="px-4 py-3 font-medium">Device / Inventory</th>
                  {STEPS.map((s) => (
                    <th key={s.key} className="px-3 py-3 font-medium whitespace-nowrap">{s.label}</th>
                  ))}
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {rows.map((row) => {
                  const chip = overallChip(row);
                  const heartbeatPos = fmtPos(row.last_position);
                  return (
                    <tr key={row.device_id} className="hover:bg-slate-700/30 align-top">
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/devices/${row.device_id}`}
                          state={{ from: backTo }}
                          className="font-mono text-cyan-300 hover:text-cyan-200 hover:underline"
                          title="Open this device's detail page"
                        >
                          {row.wifi_mac_address ?? row.serial_number}
                        </Link>
                        <div className="text-slate-400 text-xs mt-0.5">{row.user_email ?? (row.last_event_at ? 'no account yet' : row.shipped_at ? `shipped ${fmtTime(row.shipped_at)}` : 'in inventory')}</div>
                        {row.package_name && <div className="text-slate-500 text-xs">{row.package_name}</div>}
                        <InventoryLink row={row} />
                        <AnomalyBadge row={row} />
                        {row.last_heartbeat_at && (
                          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1" title={`Setup heartbeat ${fmtTime(row.last_heartbeat_at)}`}>
                            <MapPinIcon className="w-3 h-3" />
                            {heartbeatPos ?? 'no fix'} · {fmtTime(row.last_heartbeat_at)}
                          </div>
                        )}
                      </td>
                      {STEPS.map((s) => (
                        <td key={s.key} className="px-3 py-3">
                          <StepCell row={row} step={s.key} onShowMap={setMapRow} />
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${chip.cls}`}>
                          {chip.label}
                        </span>
                        {row.completed_at && (
                          <div
                            className="text-[11px] text-slate-500 mt-1"
                            title={row.admin_acceptance_estimated_at ? `Estimated tap time · admin recorded it ${fmtTime(row.completed_at)}` : undefined}
                          >
                            {row.admin_acceptance_estimated_at ? `≈ ${fmtTime(row.admin_acceptance_estimated_at)}` : fmtTime(row.completed_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5">
                          {row.reset_pending ? (
                            // A queued reset needs a way out: it only lands
                            // when the Scout is online, and it forgets the
                            // device's networks — so one left queued against a
                            // just-reset unit fires mid-walk and wipes the
                            // customer's progress.
                            <button
                              onClick={() => void handleCancelReset(row)}
                              disabled={cancelBusyId === row.device_id}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs hover:bg-amber-500/20 disabled:opacity-40 whitespace-nowrap"
                              title="Withdraw the queued reset — the Scout keeps its current setup progress"
                            >
                              <XCircleIcon className="w-3.5 h-3.5" />
                              {cancelBusyId === row.device_id ? 'Cancelling…' : 'Cancel reset'}
                            </button>
                          ) : (
                            <button
                              onClick={() => setResetTarget(row)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-500/30 bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20 whitespace-nowrap"
                            >
                              <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                              Reset to out-of-box
                            </button>
                          )}
                          {canRecordAcceptance(row) && (
                            <button
                              onClick={() => { setAcceptNote(''); setAcceptTarget(row); }}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-green-500/30 bg-green-500/10 text-green-400 text-xs hover:bg-green-500/20 whitespace-nowrap"
                              title="The customer accepted the terms on the Scout but the event never reached us — record it here, marked as admin-recorded"
                            >
                              <CheckBadgeIcon className="w-3.5 h-3.5" />
                              Record acceptance
                            </button>
                          )}
                          <button
                            onClick={() => void handleUnlockCode(row)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-600 bg-slate-800/60 text-slate-300 text-xs hover:border-slate-500 whitespace-nowrap"
                            title="Daily support bypass code — works offline"
                          >
                            <KeyIcon className="w-3.5 h-3.5" />
                            Unlock code
                          </button>
                          <button
                            onClick={() => setHistoryRow(row)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-600 bg-slate-800/60 text-slate-300 text-xs hover:border-slate-500 whitespace-nowrap"
                            title="Every run this Scout has been through, including attempts a reset replaced"
                          >
                            <ClockIcon className="w-3.5 h-3.5" />
                            History
                          </button>
                          <button
                            onClick={() => setDeleteTarget(row)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-700 bg-slate-800/40 text-slate-400 text-xs hover:border-red-500/40 hover:text-red-400 whitespace-nowrap"
                            title="Erase this fleet row — for clearing bench and test units out of the list"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary + pagination */}
      {!isLoading && !isError && (
        <div className="flex items-center justify-between flex-wrap gap-3 text-sm text-slate-400">
          <span>{total} Scout{total === 1 ? '' : 's'} in segment</span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <span>Page {page} of {totalPages}</span>
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-2 py-1 rounded border border-slate-700 disabled:opacity-40 hover:border-slate-600"
              >
                Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-2 py-1 rounded border border-slate-700 disabled:opacity-40 hover:border-slate-600"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Typed-confirmation reset modal */}

      {mapRow && (() => {
        const wifi = mapRow.steps.wifi;
        const exact = wifi?.position ?? null;
        const pos = exact ?? mapRow.last_position;
        if (!pos) return null;
        return (
          <ModalShell
            open
            onClose={() => setMapRow(null)}
            title={`Where ${mapRow.wifi_mac_address ?? mapRow.serial_number} came online`}
            size="2xl"
          >
            <div className="p-6">
              {/* An approximate pin is more use than none, but it must never be
                  mistaken for the fix the Wi-Fi step was actually stamped with. */}
              {exact ? (
                <p className="mb-4 text-sm text-slate-400">
                  Position stamped when Wi-Fi was confirmed
                  {cellSsid(wifi) ? <> on <span className="text-slate-200">{cellSsid(wifi)}</span></> : null}
                  {' · '}{fmtTime(wifi?.occurred_at)}
                </p>
              ) : (
                <p className="mb-4 text-sm text-amber-400">
                  Approximate — this Scout had no GPS fix when it came online, so this is its last
                  known position ({fmtTime(mapRow.last_position?.timestamp ?? mapRow.last_heartbeat_at)}),
                  not where Wi-Fi was confirmed.
                </p>
              )}

              <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500 block">Latitude</span>
                  <span className="text-white font-mono">{pos.lat.toFixed(6)}°</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Longitude</span>
                  <span className="text-white font-mono">{pos.lon.toFixed(6)}°</span>
                </div>
                {typeof pos.accuracy === 'number' && (
                  <div>
                    <span className="text-slate-500 block">Accuracy</span>
                    <span className="text-white">±{pos.accuracy.toFixed(1)}m</span>
                  </div>
                )}
                <div>
                  <span className="text-slate-500 block">Source</span>
                  <span className={exact ? 'text-green-400' : 'text-amber-400'}>
                    {exact ? 'Wi-Fi step' : 'last known'}
                  </span>
                </div>
              </div>

              <div className="relative w-full h-[500px] bg-slate-900 rounded-lg overflow-hidden">
                <iframe
                  className="w-full h-full"
                  title="Location"
                  frameBorder="0"
                  style={{ border: 0 }}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${pos.lat},${pos.lon}&t=k&z=15&output=embed`}
                  allowFullScreen
                />
              </div>

              <div className="mt-4 flex justify-end">
                <a
                  href={`https://www.google.com/maps?q=${pos.lat},${pos.lon}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Open in Google Maps
                </a>
              </div>
            </div>
          </ModalShell>
        );
      })()}

      {historyRow && (
        <RunHistoryModal row={historyRow} onClose={() => setHistoryRow(null)} />
      )}
      <ModalShell
        open={acceptTarget !== null}
        onClose={() => !acceptBusy && setAcceptTarget(null)}
        title="Record terms acceptance"
        closeDisabled={acceptBusy}
        footer={(
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setAcceptTarget(null)}
              disabled={acceptBusy}
              className="px-3 py-1.5 rounded border border-slate-600 text-slate-300 text-sm hover:border-slate-500 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleRecordAcceptance()}
              disabled={acceptBusy}
              className="px-3 py-1.5 rounded bg-green-600 text-white text-sm hover:bg-green-500 disabled:opacity-40"
            >
              {acceptBusy ? 'Recording…' : 'Record acceptance'}
            </button>
          </div>
        )}
      >
        <div className="space-y-3 text-sm text-slate-300">
          <p>
            This marks step 6 done for <span className="font-mono text-white">{acceptTarget?.serial_number}</span>
            {acceptTarget?.user_email ? <> (signed in as <span className="text-white">{acceptTarget.user_email}</span>)</> : null} and
            completes its setup in the funnel.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-slate-400">
            <li>Only do this when the customer has told you they accepted the terms on the Scout.</li>
            <li>The event is stored as <span className="text-slate-200">admin-recorded</span>, with your account, the time you record it and the note below — it is never shown as the Scout&rsquo;s own proof.</li>
            <li>Where the Scout&rsquo;s own traffic pins down when the customer tapped OK (the plan event that arrived late, carried by that tap), that moment is used as the acceptance time and shown with &ldquo;≈&rdquo;; otherwise the acceptance time is now.</li>
            <li>The row, the run history and the device audit trail all carry that marker.</li>
          </ul>
          <label className="block">
            <span className="block text-xs text-slate-400 mb-1">Note (how the acceptance was confirmed)</span>
            <textarea
              value={acceptNote}
              onChange={(e) => setAcceptNote(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="e.g. Customer confirmed by phone 10 Sep that they tapped OK on the care & terms card"
              className="w-full rounded border border-slate-600 bg-slate-900/60 px-2 py-1.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-cyan-500"
            />
          </label>
        </div>
      </ModalShell>
      <TypedConfirmModal
        open={resetTarget !== null}
        onClose={() => !resetBusy && setResetTarget(null)}
        onConfirm={handleReset}
        busy={resetBusy}
        title="Reset to out-of-box"
        confirmPhrase={`RESET ${resetTarget?.serial_number ?? ''}`}
        confirmLabel="Queue reset"
      >
        <p>
          This returns <span className="font-mono text-white">{resetTarget?.serial_number}</span> to factory-fresh
          state: the customer must redo all six setup steps and the Scout is unusable until they do.
        </p>
        <ul className="list-disc pl-5 space-y-1 text-slate-400">
          <li>The account link, subscription link, and device token are severed server-side.</li>
          <li>It applies when the Scout next checks in — offline devices show &ldquo;reset pending&rdquo; until then.</li>
          <li>Setup history is kept in the audit trail under the previous run.</li>
        </ul>
      </TypedConfirmModal>
      <TypedConfirmModal
        open={deleteTarget !== null}
        onClose={() => !deleteBusy && setDeleteTarget(null)}
        onConfirm={handleDelete}
        busy={deleteBusy}
        title="Delete this device from the fleet"
        confirmPhrase={`DELETE ${deleteTarget?.serial_number ?? ''}`}
        confirmLabel="Delete device"
      >
        <p>
          This erases the fleet row for <span className="font-mono text-white">{deleteTarget?.serial_number}</span>{' '}
          — for clearing bench and test units out of this list.
        </p>
        <ul className="list-disc pl-5 space-y-1 text-slate-400">
          <li>Erased with it: entitlements, queued commands, offline packs, telemetry, position and NMEA history, and its setup runs.</li>
          <li>Kept: the inventory/build record for the unit, and the audit trail of what was done to it.</li>
          <li>
            <span className="text-amber-300">This does not disable the Scout.</span> If it is still powered on and
            reaches the internet, it re-appears here from its build record.
          </li>
          {deleteTarget?.user_email && (
            <li className="text-amber-300">
              Registered to {deleteTarget.user_email}
              {deleteTarget.has_active_plan ? ' with an active plan' : ''} — the account and its subscription are
              untouched, but this unit loses its link to them.
            </li>
          )}
        </ul>
      </TypedConfirmModal>
    </div>
  );
}

/**
 * The inventory linkage — the answer to "which unit in stock is this?".
 * Present whenever the device resolved to a stock item via its MAC; the
 * amber note flags a unit that is live on the internet while the build
 * board still has it on the bench, which is usually a paperwork gap.
 */
function AnomalyBadge({ row }: { row: OnboardingFunnelRow }) {
  if (!row.inventory_anomaly) return null;
  const copy = ANOMALY_COPY[row.inventory_anomaly];
  if (!copy) return null;
  return (
    <div
      className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 text-[11px] font-medium"
      title={copy.detail}
    >
      <ExclamationTriangleIcon className="w-3 h-3" />
      {copy.label}
    </div>
  );
}

function InventoryLink({ row }: { row: OnboardingFunnelRow }) {
  if (!row.stock_item_id) {
    return row.wifi_mac_address ? (
      <div className="text-[11px] text-slate-600 mt-1">No inventory record for this MAC</div>
    ) : null;
  }
  const model = row.stock_attributes?.model;
  // Suppressed when an anomaly badge already says it, to avoid saying it twice.
  const liveButNotShipped = row.inventory_anomaly == null
    && row.stock_state != null && !['shipped', 'deployed', 'retired'].includes(row.stock_state);
  return (
    <div className="mt-1 space-y-0.5">
      <Link
        to={`/admin/inventory?search=${encodeURIComponent(row.wifi_mac_address ?? '')}`}
        className="inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300"
      >
        <ArchiveBoxIcon className="w-3 h-3" />
        {row.batch_tag ?? 'inventory'}{row.stock_state ? ` · ${row.stock_state.replace(/_/g, ' ')}` : ''}
      </Link>
      {model && <div className="text-[11px] text-slate-600">{model}</div>}
      {liveButNotShipped && (
        <div className="text-[11px] text-amber-400">Live on the internet but stock says &ldquo;{row.stock_state?.replace(/_/g, ' ')}&rdquo;</div>
      )}
    </div>
  );
}

export default AdminOnboardingPage;

/**
 * Every run this device has been through, newest first.
 *
 * The funnel deliberately shows only the current run, so an out-of-box reset
 * makes the previous attempt vanish from view even though every event is still
 * stored. This is how that evidence comes back — what the Scout did last time,
 * when, and where.
 */
function RunHistoryModal({ row, onClose }: { row: OnboardingFunnelRow; onClose: () => void }) {
  const { data: runs, isLoading, error } = useQuery({
    queryKey: ['admin', 'onboarding', 'runs', row.device_id],
    queryFn: () => api.adminGetOnboardingRuns(row.device_id),
  });

  return (
    <ModalShell
      open
      onClose={onClose}
      title={`Setup history — ${row.wifi_mac_address ?? row.serial_number}`}
      size="2xl"
    >
      <div className="p-6 space-y-6">
        {isLoading && <p className="text-slate-400 text-sm">Loading history…</p>}
        {error && <p className="text-red-400 text-sm">Could not load history.</p>}
        {runs && runs.length === 0 && (
          <p className="text-slate-400 text-sm">
            This Scout has never reported a setup event — it has not booted, or never reached the internet.
          </p>
        )}
        {runs?.map((run: OnboardingRun, idx: number) => (
          <div key={run.run_id} className="rounded-lg border border-slate-700 bg-slate-900/40">
            <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-slate-700">
              <div className="text-sm">
                <span className="text-white font-semibold">
                  {idx === 0 ? 'Current run' : `Earlier run ${runs.length - idx}`}
                </span>
                <span className="text-slate-500 ml-2 font-mono text-xs">{run.run_id.slice(0, 8)}</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className={run.completed ? 'text-green-400' : 'text-amber-400'}>
                  {run.completed ? 'completed' : 'did not complete'}
                </span>
                <span className="text-slate-500">
                  {fmtTime(run.started_at)} → {fmtTime(run.last_event_at)}
                </span>
              </div>
            </div>
            <table className="w-full text-xs">
              <tbody>
                {run.events.map((e, i) => (
                  <tr key={`${e.run_id}-${e.step}-${e.status}-${i}`} className="border-b border-slate-800 last:border-0">
                    <td className="px-4 py-1.5 text-slate-300 w-28 capitalize">{e.step}</td>
                    <td className="px-2 py-1.5 w-32">
                      <span className={
                        e.status === 'verified' ? 'text-green-400'
                          : e.status === 'bypassed' ? 'text-amber-400'
                            : e.status === 'stalled' ? 'text-red-400' : 'text-cyan-300'
                      }>
                        {e.status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{fmtTime(e.occurred_at)}</td>
                    <td className="px-2 py-1.5 text-slate-500 font-mono">{fmtPos(e.position) ?? ''}</td>
                    <td className="px-4 py-1.5 text-slate-400 truncate max-w-[14rem]">
                      {recordedByAdmin(e.details)
                        ? (() => {
                            const est = estimatedAcceptanceAt(e, run.events.find((p) => p.step === 'plan' && p.status === 'verified'));
                            const recordedAt = typeof e.details?.recorded_at === 'string' ? e.details.recorded_at : e.occurred_at;
                            const admin = typeof e.details?.admin_email === 'string' ? e.details.admin_email : 'admin';
                            const note = typeof e.details?.note === 'string' && e.details.note ? ` — ${e.details.note}` : '';
                            return `${est ? `≈ tap ${fmtTime(est)} (estimated from plan delivery) · ` : ''}recorded by ${admin} on ${fmtTime(recordedAt)}${note}`;
                          })()
                        : (cellSsid(e) ?? (typeof e.details?.kind === 'string' ? String(e.details.kind) : ''))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </ModalShell>
  );
}
